import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Undo2, History, Info } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { handleApiError } from "@/utils";
import {
  useGetConfigurationSettings,
  useUpdateConfigurationSettings,
  useGetConfigurationSettingChanges,
  useRecalcPendingKps,
} from "@/resources/configuration-settings/configuration-settings.api";
import { useGetKpsRecalculation, useStartKpsRecalculation } from "@/resources/kps-recalculation";
import type { ScoringWeights, KeywordSalesTier, HeroRankTier, TrendTier, PenaltySettings, RelevanceConfig, Snapshot, FactorKey } from "./kps-configuration/types";
import {
  defaultWeights,
  defaultRelevance,
  defaultTrendTiers,
  defaultPenalties,
  defaultAsinDensityMax,
  defaultKeywordSalesMax,
  defaultCompetitionMax,
  defaultFactorEnabled,
  defaultKeywordSalesTiers,
  defaultHeroRankTiers,
  BRAND_INTELLIGENCE_ALIASES,
} from "./kps-configuration/constants";
import { settingsToMap, applySettingsToState, stateToSettings, groupChangesIntoEntries } from "./kps-configuration/utils";
import { hasTriggeringChange } from "./kps-configuration/recalculation-triggers";
import { RecalculationConfirmDialog } from "./kps-configuration/RecalculationConfirmDialog";
import { ScoringWeightsCard } from "./kps-configuration/ScoringWeightsCard";
import { SerpMatchScoringCard } from "./kps-configuration/SerpMatchScoringCard";
import { RelevanceScoringCard } from "./kps-configuration/RelevanceScoringCard";
import { SearchVolumeScoringCard } from "./kps-configuration/SearchVolumeScoringCard";
import { HeroRankScoringCard } from "./kps-configuration/HeroRankScoringCard";
import { AsinDensityScoringCard } from "./kps-configuration/AsinDensityScoringCard";
import { KeywordSalesScoringCard } from "./kps-configuration/KeywordSalesScoringCard";
import { CompetitionScoringCard } from "./kps-configuration/CompetitionScoringCard";
import { SearchTrendScoringCard } from "./kps-configuration/SearchTrendScoringCard";
import { KPSPreviewCard } from "./kps-configuration/KPSPreviewCard";
import { SaveBar } from "./kps-configuration/SaveBar";
import { ChangeHistoryDialog } from "./kps-configuration/ChangeHistoryDialog";

export function KPSConfigurationTab() {
  const { data: configData, isLoading } = useGetConfigurationSettings();
  const updateMutation = useUpdateConfigurationSettings();
  const recalcPendingKps = useRecalcPendingKps();
  const { data: changeHistory, isLoading: isChangesLoading } = useGetConfigurationSettingChanges();
  // FR-1: the recalculation-owed notice and confirmation flow are visible to every admin
  // who can reach this tab (it renders only under AdminRoute), so this poll is
  // unconditionally enabled -- it shares its query key/cache with WEB-003's app-wide
  // poller in ProtectedRoute rather than issuing a second request.
  const { data: recalcStatus } = useGetKpsRecalculation(true);
  const startRecalculation = useStartKpsRecalculation();
  const [weights, setWeights] = useState<ScoringWeights>({ ...defaultWeights });
  const [asinDensityMax, setAsinDensityMax] = useState<number>(defaultAsinDensityMax);
  const [keywordSalesMax, setKeywordSalesMax] = useState<number>(defaultKeywordSalesMax);
  const [competitionMax, setCompetitionMax] = useState<number>(defaultCompetitionMax);
  const [factorEnabled, setFactorEnabled] = useState<Record<FactorKey, boolean>>({ ...defaultFactorEnabled });
  const [keywordSalesTiers, setKeywordSalesTiers] = useState<KeywordSalesTier[]>(defaultKeywordSalesTiers.map((t) => ({ ...t })));
  const [heroRankTiers, setHeroRankTiers] = useState<HeroRankTier[]>(defaultHeroRankTiers.map((t) => ({ ...t })));
  const [trendTiers, setTrendTiers] = useState<TrendTier[]>(defaultTrendTiers.map((t) => ({ ...t })));
  const [penalties, setPenalties] = useState<PenaltySettings>({ ...defaultPenalties });
  const [relevance, setRelevance] = useState<RelevanceConfig>({ ...defaultRelevance, heroComponents: [...defaultRelevance.heroComponents] });
  const [serpMatch, setSerpMatch] = useState({ cutoffMultiplier: 0.8, anchorTopN: 20 });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [undoStack, setUndoStack] = useState<Snapshot[]>([]);
  const [recalculationConfirmOpen, setRecalculationConfirmOpen] = useState(false);
  // FR-1: "the values the tab loaded", captured once at load and never re-derived from
  // form state, so a later Undo/Reset round-trip back to the loaded values is not
  // mistaken for a change.
  const loadedSettingsMapRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!configData || initialized) return;
    const m = settingsToMap(configData);
    loadedSettingsMapRef.current = m;
    const state = applySettingsToState(m);
    setWeights(state.weights);
    setAsinDensityMax(state.asinDensityMax);
    setKeywordSalesMax(state.keywordSalesMax);
    setCompetitionMax(state.competitionMax);
    setKeywordSalesTiers(state.keywordSalesTiers);
    setHeroRankTiers(state.heroRankTiers);
    setTrendTiers(state.trendTiers);
    setPenalties(state.penalties);
    setRelevance(state.relevance);
    setSerpMatch(state.serpMatch);
    setFactorEnabled(state.factorEnabled);
    setInitialized(true);
  }, [configData, initialized]);
  const captureSnapshot = useCallback((): Snapshot => ({
    weights: { ...weights },
    asinDensityMax,
    keywordSalesMax,
    competitionMax,
    keywordSalesTiers: keywordSalesTiers.map((t) => ({ ...t })),
    heroRankTiers: heroRankTiers.map((t) => ({ ...t })),
    trendTiers: trendTiers.map((t) => ({ ...t })),
    penalties: { ...penalties },
    relevance: { ...relevance, heroComponents: [...relevance.heroComponents] },
    factorEnabled: { ...factorEnabled },
    serpMatch: { ...serpMatch },
  }), [serpMatch, weights, asinDensityMax, keywordSalesMax, competitionMax, keywordSalesTiers, heroRankTiers, trendTiers, penalties, relevance, factorEnabled]);

  const pushUndo = useCallback(() => {
    setUndoStack((prev) => [...prev.slice(-49), captureSnapshot()]);
  }, [captureSnapshot]);
  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    const newStack = undoStack.slice(0, -1);
    setUndoStack(newStack);
    setWeights(prev.weights);
    setAsinDensityMax(prev.asinDensityMax);
    setKeywordSalesMax(prev.keywordSalesMax);
    setCompetitionMax(prev.competitionMax);
    setKeywordSalesTiers(prev.keywordSalesTiers);
    setHeroRankTiers(prev.heroRankTiers);
    setTrendTiers(prev.trendTiers);
    setPenalties(prev.penalties);
    setRelevance(prev.relevance);
    setSerpMatch(prev.serpMatch);
    setFactorEnabled(prev.factorEnabled);
    if (newStack.length === 0) setHasChanges(false);
    toast("Change Undone", { description: "Reverted to previous state." });
  };
  const handleReset = () => {
    pushUndo();
    setWeights({ ...defaultWeights });
    setAsinDensityMax(defaultAsinDensityMax);
    setKeywordSalesMax(defaultKeywordSalesMax);
    setCompetitionMax(defaultCompetitionMax);
    setKeywordSalesTiers(defaultKeywordSalesTiers.map((t) => ({ ...t })));
    setHeroRankTiers(defaultHeroRankTiers.map((t) => ({ ...t })));
    setTrendTiers(defaultTrendTiers.map((t) => ({ ...t })));
    setPenalties({ ...defaultPenalties });
    setRelevance({ ...defaultRelevance, heroComponents: [...defaultRelevance.heroComponents] });
    // AUT-496: provisional until the Vertex calibration; restoring defaults restores these too.
    setSerpMatch({ cutoffMultiplier: 0.8, anchorTopN: 20 });
    setFactorEnabled({ ...defaultFactorEnabled });
    // AUT-200/AUT-232: restore defaults into the form but do NOT auto-persist -- let
    // the admin review and Save through the same gate as any other edit. With Hero
    // Rank off by default the enabled weights already total 100, so Save is available
    // immediately; we still don't persist here so applying defaults stays deliberate.
    setHasChanges(true);
    toast.success("Defaults restored", { description: "Weights total 100%. Review and Save to apply." });
  };
  const buildSettings = () =>
    stateToSettings(weights, asinDensityMax, keywordSalesMax, competitionMax, keywordSalesTiers, heroRankTiers, trendTiers, penalties, relevance, serpMatch, factorEnabled);

  // FR-1: only when a trigger alias (the 34 CALC_KPS_FOR_KEYWORDS reads, minus the 3
  // excluded aliases -- see recalculation-triggers.ts) differs from the loaded value does
  // Save open the confirmation modal instead of saving directly.
  const handleSave = () => {
    const settings = buildSettings();
    if (hasTriggeringChange(settings, loadedSettingsMapRef.current)) {
      setRecalculationConfirmOpen(true);
      return;
    }
    updateMutation.mutate(
      { settings },
      {
        onSuccess: () => {
          toast.success("Configuration Saved", { description: "KPS scoring configuration has been updated." });
          setHasChanges(false);
        },
      },
    );
  };

  // FR-4: PUT then, only on its success, POST -- two requests, in that order. The PUT's
  // own onError (defined on useUpdateConfigurationSettings) already toasts a 409's
  // message; POST has no such default (kps-recalculation.api.ts), so its 409/422/500 is
  // handled here.
  const handleConfirmSaveAndRecalculate = () => {
    const settings = buildSettings();
    updateMutation.mutate(
      { settings },
      {
        onSuccess: () => {
          toast.success("Configuration Saved", { description: "KPS scoring configuration has been updated." });
          setHasChanges(false);
          startRecalculation.mutate(undefined, {
            onError: (error) => handleApiError(error),
          });
        },
        onSettled: () => setRecalculationConfirmOpen(false),
      },
    );
  };

  const markChanged = () => setHasChanges(true);
  const clampPercent = (value: number) => Math.min(100, Math.max(0, value));

  const toggleFactor = (key: FactorKey) => (value: boolean) => {
    pushUndo();
    setFactorEnabled((prev) => ({ ...prev, [key]: value }));
    markChanged();
  };

  const updateWeight = (key: keyof ScoringWeights, value: number) => {
    pushUndo();
    const clamped = clampPercent(value);
    setWeights((prev) => ({ ...prev, [key]: clamped }));
    if (key === "searchVolumeTrend") {
      setTrendTiers((prev) => prev.map((t) => (t.points > clamped ? { ...t, points: clamped } : t)));
    }
    if (key === "heroRank") {
      setHeroRankTiers((prev) => prev.map((t) => (t.points > clamped ? { ...t, points: clamped } : t)));
    }
    markChanged();
  };

  const distributionItems = [
    { moduleId: "kps-module-relevance", key: "relevance" as FactorKey, label: "Relevance", value: weights.relevance, max: 37.5, color: "bg-chart-6" },
    { moduleId: "kps-module-search-volume", key: "searchVolume" as FactorKey, label: "Search Volume", value: weights.searchVolume, max: 15, color: "bg-chart-1" },
    { moduleId: "kps-module-hero-rank", key: "heroRank" as FactorKey, label: "Hero Rank", value: weights.heroRank, max: 15, color: "bg-chart-2" },
    { moduleId: "kps-module-asin-density", key: "asinDensity" as FactorKey, label: "ASIN Density", value: asinDensityMax, max: 15, color: "bg-chart-3" },
    { moduleId: "kps-module-keyword-sales", key: "keywordSales" as FactorKey, label: "Keyword Sales", value: keywordSalesMax, max: 10, color: "bg-chart-4" },
    { moduleId: "kps-module-competition", key: "competition" as FactorKey, label: "Competition", value: competitionMax, max: 15, color: "bg-chart-7" },
    { moduleId: "kps-module-search-trend", key: "searchTrend" as FactorKey, label: "Search Trend", value: weights.searchVolumeTrend, max: 7.5, color: "bg-chart-5" },
  ].filter((item) => factorEnabled[item.key]);
  const distributionTotal = distributionItems.reduce((sum, i) => sum + i.value, 0);

  const scrollToModule = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // AUT-362: the change log is shared with Brand Intelligence, so drop only Brand Intelligence's
  // own settings here. Complement, not allow-list: aliases with no ALIAS_META entry (other tabs')
  // must still surface under "Other" rather than vanish from every dialog.
  const changeLogEntries = useMemo(
    () => (changeHistory ? groupChangesIntoEntries(changeHistory.filter((c) => !BRAND_INTELLIGENCE_ALIASES.has(c.alias))) : []),
    [changeHistory],
  );

  // `recalculation_owed` is already false while a run is queued/running
  // (`components/keyword.md § GET /api/kps-recalculation`); the run-status check here is
  // a defensive restatement of that same invariant, not a second source of truth.
  const showRecalculationOwedNotice =
    !!recalcStatus?.recalculation_owed &&
    recalcStatus.run?.status !== "queued" &&
    recalcStatus.run?.status !== "running";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-muted-foreground">Loading configuration...</div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6 px-px">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 rounded-lg bg-card" onClick={handleUndo} disabled={undoStack.length === 0}>
              <Undo2 className="h-3.5 w-3.5" />
              Undo
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 rounded-lg bg-card" onClick={() => setHistoryOpen(true)}>
              <History className="h-3.5 w-3.5" />
              Change History
            </Button>
          </div>
        </div>

        {showRecalculationOwedNotice && (
          <Alert variant="info" data-testid="kps-recalculation-owed-notice">
            <Info className="h-4 w-4" />
            <AlertDescription>
              Stored KPS scores are out of date. Save the configuration again to recalculate.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <div className="flex flex-col gap-6">
            <ScoringWeightsCard
              distributionItems={distributionItems}
              distributionTotal={distributionTotal}
              scrollToModule={scrollToModule}
            />

            <RelevanceScoringCard
              relevanceWeight={weights.relevance}
              onRelevanceWeightChange={(v) => updateWeight("relevance", v)}
              penalties={penalties}
              onPenaltyChange={(key, v) => {
                pushUndo();
                setPenalties((p) => ({ ...p, [key]: v }));
                markChanged();
              }}
              relevance={relevance}
              onToggleComponent={(comp) => {
                pushUndo();
                setRelevance((p) => ({
                  ...p,
                  heroComponents: p.heroComponents.includes(comp)
                    ? p.heroComponents.filter((c) => c !== comp)
                    : [...p.heroComponents, comp],
                }));
                markChanged();
              }}
              enabled={factorEnabled.relevance}
              onEnabledChange={toggleFactor("relevance")}
            />

            <SerpMatchScoringCard
              value={serpMatch}
              onChange={(next) => {
                setSerpMatch(next);
                setHasChanges(true);
              }}
            />

            <SearchVolumeScoringCard
              searchVolumeWeight={weights.searchVolume}
              onSearchVolumeWeightChange={(v) => updateWeight("searchVolume", v)}
              enabled={factorEnabled.searchVolume}
              onEnabledChange={toggleFactor("searchVolume")}
            />

            <HeroRankScoringCard
              heroRankWeight={weights.heroRank}
              onHeroRankWeightChange={(v) => updateWeight("heroRank", v)}
              heroRankTiers={heroRankTiers}
              onTierPointsChange={(idx, v) => {
                pushUndo();
                setHeroRankTiers((prev) => prev.map((r, i) => (i === idx ? { ...r, points: v } : r)));
                markChanged();
              }}
              enabled={factorEnabled.heroRank}
              onEnabledChange={toggleFactor("heroRank")}
            />

            <AsinDensityScoringCard
              asinDensityMax={asinDensityMax}
              onAsinDensityMaxChange={(v) => {
                pushUndo();
                setAsinDensityMax(v);
                markChanged();
              }}
              enabled={factorEnabled.asinDensity}
              onEnabledChange={toggleFactor("asinDensity")}
            />

            <KeywordSalesScoringCard
              keywordSalesMax={keywordSalesMax}
              onKeywordSalesMaxChange={(newMax) => {
                pushUndo();
                setKeywordSalesMax(newMax);
                setKeywordSalesTiers((prev) => prev.map((t) => (t.points > newMax ? { ...t, points: newMax } : t)));
                markChanged();
              }}
              keywordSalesTiers={keywordSalesTiers}
              onTierPointsChange={(idx, v) => {
                pushUndo();
                setKeywordSalesTiers((prev) => prev.map((r, i) => (i === idx ? { ...r, points: v } : r)));
                markChanged();
              }}
              enabled={factorEnabled.keywordSales}
              onEnabledChange={toggleFactor("keywordSales")}
            />

            <CompetitionScoringCard
              competitionMax={competitionMax}
              onCompetitionMaxChange={(v) => {
                pushUndo();
                setCompetitionMax(v);
                markChanged();
              }}
              enabled={factorEnabled.competition}
              onEnabledChange={toggleFactor("competition")}
            />

            <SearchTrendScoringCard
              searchVolumeTrendWeight={weights.searchVolumeTrend}
              onSearchVolumeTrendWeightChange={(v) => updateWeight("searchVolumeTrend", v)}
              trendTiers={trendTiers}
              onTierMaxBoundChange={(i, v) => {
                pushUndo();
                setTrendTiers((prev) => prev.map((t, j) => (j === i ? { ...t, maxBound: v } : t)));
                markChanged();
              }}
              onTierPointsChange={(i, v) => {
                pushUndo();
                setTrendTiers((prev) => prev.map((t, j) => (j === i ? { ...t, points: v } : t)));
                markChanged();
              }}
              enabled={factorEnabled.searchTrend}
              onEnabledChange={toggleFactor("searchTrend")}
            />
          </div>

          <div className="space-y-6">
            <KPSPreviewCard
              weights={weights}
              asinDensityMax={asinDensityMax}
              competitionMax={competitionMax}
              keywordSalesTiers={keywordSalesTiers}
              heroRankTiers={heroRankTiers}
              trendTiers={trendTiers}
              distributionItems={distributionItems}
              factorEnabled={factorEnabled}
            />
          </div>
        </div>

        {hasChanges && (
          <SaveBar
            distributionTotal={distributionTotal}
            isSaving={updateMutation.isPending}
            onReset={handleReset}
            onSave={handleSave}
          />
        )}

        <ChangeHistoryDialog
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          isLoading={isChangesLoading}
          entries={changeLogEntries}
          description="A log of every KPS scoring change — weights, tier points, and thresholds."
        />

        <RecalculationConfirmDialog
          open={recalculationConfirmOpen}
          onOpenChange={setRecalculationConfirmOpen}
          onConfirm={handleConfirmSaveAndRecalculate}
          isPending={updateMutation.isPending || startRecalculation.isPending}
        />
      </div>
    </TooltipProvider>
  );
}
